import React from "react";
import {
  FileText,
  ListChecks,
  Package,
  TrendingDown,
  Boxes,
} from "lucide-react";
import type { ProductTabsProps } from "./types";

const ProductTabs: React.FC<ProductTabsProps> = ({
  product,
  selectedTab,
  onTabChange,
  hasDiscount,
  originalPrice,
}) => {
  const isValidWeight =
    typeof product.weight === "number" && product.weight > 0;

  return (
    <div className="bg-neutral-900 rounded-xl shadow-sm mb-8 border border-neutral-800 overflow-hidden">
      {/* Tabs Navigation */}
      <div className="border-b border-neutral-800">
        <nav className="flex px-6 -mb-px">
          <button
            onClick={() => onTabChange("description")}
            className={`relative py-4 px-4 font-medium text-sm transition-colors flex items-center gap-2 ${
              selectedTab === "description"
                ? "text-primary"
                : "text-neutral-400 hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Description</span>
            {selectedTab === "description" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => onTabChange("specifications")}
            className={`relative py-4 px-4 font-medium text-sm transition-colors flex items-center gap-2 ${
              selectedTab === "specifications"
                ? "text-primary"
                : "text-neutral-400 hover:text-white"
            }`}
          >
            <ListChecks className="w-4 h-4" />
            <span>Specifications</span>
            {selectedTab === "specifications" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </nav>
      </div>

      <div className="p-6">
        {selectedTab === "description" && (
          <div>
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Product Description
            </h3>
            <p className="text-neutral-300 leading-relaxed text-base">
              {product.description ||
                "No description available for this product."}
            </p>
          </div>
        )}

        {selectedTab === "specifications" && (
          <div>
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-primary" />
              Product Specifications
            </h3>

            {/* Key Specs Highlight */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2 text-blue-400 mb-2">
                  <Package className="w-5 h-5" />
                  <span className="text-sm font-medium">Price</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  ${originalPrice.toFixed(2)}
                </p>
              </div>

              {hasDiscount && (
                <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/20">
                  <div className="flex items-center gap-2 text-red-500 mb-2">
                    <TrendingDown className="w-5 h-5" />
                    <span className="text-sm font-medium">Discount</span>
                  </div>
                  <p className="text-2xl font-bold text-red-500">
                    {product.sale_rate}% OFF
                  </p>
                </div>
              )}

              <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <Boxes className="w-5 h-5" />
                  <span className="text-sm font-medium">Stock</span>
                </div>
                {(() => {
                  const qty =
                    typeof product.quantity === "number" ? product.quantity : 0;
                  if (qty === 0) {
                    return (
                      <p className="text-2xl font-bold text-red-500">
                        Out of Stock
                      </p>
                    );
                  }
                  if (qty < 5) {
                    return (
                      <p className="text-2xl font-bold text-amber-500">
                        Low Stock
                      </p>
                    );
                  }
                  return (
                    <p className="text-2xl font-bold text-white">{qty} Units</p>
                  );
                })()}
              </div>
            </div>

            {/* Detailed Specifications Table */}
            <div className="border border-neutral-800 rounded-lg overflow-hidden">
              <table className="w-full">
                <tbody className="divide-y divide-neutral-800">
                  <tr className="hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-400 bg-neutral-800/50 w-1/3">
                      Price
                    </td>
                    <td className="px-4 py-3 text-sm text-white font-semibold">
                      ${originalPrice.toFixed(2)}
                    </td>
                  </tr>

                  {hasDiscount && (
                    <tr className="hover:bg-neutral-800/50">
                      <td className="px-4 py-3 text-sm font-medium text-neutral-400 bg-neutral-800/50">
                        Discount
                      </td>
                      <td className="px-4 py-3 text-sm text-red-500 font-semibold">
                        {product.sale_rate}% OFF
                      </td>
                    </tr>
                  )}

                  <tr className="hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-400 bg-neutral-800/50">
                      Stock Status
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const qty =
                          typeof product.quantity === "number"
                            ? product.quantity
                            : 0;
                        if (qty === 0) {
                          return (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                              Out of stock
                            </span>
                          );
                        }
                        if (qty < 5) {
                          return (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                              Low in stock ({qty} units)
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                            In stock ({qty} units)
                          </span>
                        );
                      })()}
                    </td>
                  </tr>

                  {isValidWeight && (
                    <tr className="hover:bg-neutral-800/50">
                      <td className="px-4 py-3 text-sm font-medium text-neutral-400 bg-neutral-800/50">
                        Weight
                      </td>
                      <td className="px-4 py-3 text-sm text-white font-semibold">
                        {product.weight} Kg
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductTabs;
